import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, Loader2, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

export default function Verify() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const entitlementId = useMemo(() =>
    params.get("entitlement") || localStorage.getItem("aiude_entitlement_id") || "",
    [params]
  );
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!entitlementId) return toast.error("Missing entitlement — please start again.");
    if (!/^\d{6}$/.test(code.trim())) return toast.error("Enter the six-digit code emailed to you.");
    setLoading(true);
    try {
      const { data } = await api.post("/entitlements/verify", { entitlement_id: entitlementId, code: code.trim() });
      if (data.status === "verified") {
        localStorage.setItem("aiude_entitlement_id", entitlementId);
        localStorage.setItem("aiude_entitlement_status", "verified");
        toast.success("Email verified. Continue to specification generation.");
        nav("/intake");
      } else {
        toast.error("Verification did not complete.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Invalid or expired code.";
      toast.error(msg);
    } finally { setLoading(false); }
  };

  const resend = async () => {
    if (!entitlementId) return;
    setResending(true);
    try {
      await api.post("/entitlements/resend-verification", { entitlement_id: entitlementId });
      toast.success("If a verification step is required, we've emailed a new code.");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 503) {
        toast.error(err?.response?.data?.detail || "We couldn't send your verification email right now. Your free demonstration has not been consumed. Please try again later.");
      } else {
        toast.error(err?.response?.data?.detail || "Please try again shortly.");
      }
    } finally { setResending(false); }
  };

  return (
    <div className="max-w-md mx-auto mt-16">
      <div className="panel-elev tick-brackets p-8">
        <div className="flex items-center gap-3 mb-6">
          <KeyRound className="w-5 h-5 text-[#00D2FF]" />
          <div>
            <div className="font-tech">Stage 00 · Verify Email</div>
            <h1 className="text-xl font-semibold text-white">Enter the six-digit code</h1>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="verify-form">
          <div>
            <label className="label-cad">Verification code</label>
            <input className="input-cad font-mono text-lg tracking-[0.4em] text-center" maxLength={6}
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} data-testid="verify-code" autoComplete="one-time-code" />
          </div>
          <button className="btn-primary w-full justify-center" disabled={loading} data-testid="verify-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify email"}
          </button>
        </form>
        <button className="btn-ghost text-xs w-full justify-center mt-6" onClick={resend} disabled={resending} data-testid="verify-resend">
          {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Send a new code
        </button>
        <p className="text-slate-500 text-[11px] mt-4 leading-relaxed">
          The code expires in 24 hours and can be entered up to five times. Resending replaces the prior code.
        </p>
      </div>
    </div>
  );
}
